import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuPopover,
  MenuItem,
} from '@reach/menu-button'
import { positionRight } from '@reach/popover'
import { RenderedHtml } from 'components'
import {
  fetchLearningHubBlock,
  saveLearningHubBlockProgress,
} from 'api-actions'
import * as flashActions from 'redux-flash'

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

function formatVideoTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function LearningHubVideoBlockPanel({
  block,
  fetchLearningHubBlock: refetchBlock,
  flashErrorMessage,
}) {
  const videoRef = useRef(null)
  const didSeekFromSavedRef = useRef(false)
  const completionPostedRef = useRef(false)
  const desc = block.description
  const src = block.attachmentUrl

  const [paused, setPaused] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [muted, setMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [saving, setSaving] = useState(false)
  const savedProgressSeconds =
    block.progress?.type === 'video' ? Number(block.progress.progress) : 0

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.playbackRate = playbackRate
  }, [playbackRate])

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.muted = muted
  }, [muted])

  useEffect(() => {
    didSeekFromSavedRef.current = false
    completionPostedRef.current = false
  }, [block.id])

  const togglePlay = () => {
    const el = videoRef.current
    if (!el) return
    if (el.paused) {
      el.play().catch(() => {})
    } else {
      el.pause()
    }
  }

  const onSeek = (fraction) => {
    const el = videoRef.current
    if (!el || !Number.isFinite(duration) || duration <= 0) return
    el.currentTime = fraction * duration
  }

  const toggleMute = () => {
    setMuted((m) => !m)
  }

  const seekFraction =
    duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0

  return (
    <div className="learning-hub-block-player__panel">
      {desc ? (
        <div className="learning-hub-block-player__block-description">
          <RenderedHtml>{desc}</RenderedHtml>
        </div>
      ) : null}

      <div className="learning-hub-block-player__video-shell">
        <video
          ref={videoRef}
          key={block.id}
          className="learning-hub-block-player__video-element"
          playsInline
          preload="metadata"
          src={src}
          onClick={togglePlay}
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          onTimeUpdate={() => {
            const el = videoRef.current
            if (el) setCurrentTime(el.currentTime)
          }}
          onLoadedMetadata={() => {
            const el = videoRef.current
            if (el && Number.isFinite(el.duration)) {
              setDuration(el.duration)
              if (
                !didSeekFromSavedRef.current &&
                Number.isFinite(savedProgressSeconds) &&
                savedProgressSeconds > 0
              ) {
                el.currentTime = Math.min(
                  savedProgressSeconds,
                  el.duration || savedProgressSeconds
                )
                setCurrentTime(el.currentTime)
                didSeekFromSavedRef.current = true
              }
            }
          }}
          onDurationChange={() => {
            const el = videoRef.current
            if (el && Number.isFinite(el.duration)) setDuration(el.duration)
          }}
          onEnded={async () => {
            setPaused(true)
            if (completionPostedRef.current) return
            completionPostedRef.current = true
            const el = videoRef.current
            const t =
              el && Number.isFinite(el.currentTime)
                ? el.currentTime
                : currentTime
            try {
              await saveLearningHubBlockProgress(block.id, {
                type: 'video',
                progress: t,
                complete: true,
              })
              await refetchBlock(block.id)
            } catch (err) {
              completionPostedRef.current = false
              flashErrorMessage(
                err?.message || 'Unable to mark this lesson as complete.'
              )
            }
          }}
        />

        {paused ? (
          <button
            type="button"
            className="learning-hub-block-player__video-overlay-play"
            onClick={togglePlay}
            aria-label="Play video"
          >
            <span className="learning-hub-block-player__video-overlay-play-inner">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="29"
                height="40"
                viewBox="0 0 29 40"
                fill="none"
              >
                <path
                  d="M0.5 36.8261V2.93137C0.5 0.946106 2.77372 -0.216001 4.37016 0.994527L26.9622 17.9419C28.2684 18.9103 28.2684 20.8472 26.9622 21.8156L4.37016 38.763C2.77372 39.9735 0.5 38.8114 0.5 36.8261Z"
                  fill="#2B0AAF"
                  stroke="#2B0AAF"
                />
              </svg>
            </span>
          </button>
        ) : null}

        <div className="learning-hub-block-player__video-controls">
          <button
            type="button"
            className="learning-hub-block-player__video-icon-btn"
            onClick={togglePlay}
            aria-label={paused ? 'Play' : 'Pause'}
          >
            {paused ? (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path fill="currentColor" d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path fill="currentColor" d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            )}
          </button>

          <button
            type="button"
            className="learning-hub-block-player__video-icon-btn"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 4v-4.46l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M3 9v6h4l5 4V5L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                />
              </svg>
            )}
          </button>

          <span className="learning-hub-block-player__video-time">
            {formatVideoTime(currentTime)}
          </span>
          <input
            type="range"
            className="learning-hub-block-player__video-seek"
            style={{ '--lh-video-progress': `${seekFraction * 100}%` }}
            min={0}
            max={1000}
            step={1}
            value={Math.round(seekFraction * 1000)}
            aria-label="Seek"
            onChange={(e) => onSeek(Number(e.target.value) / 1000)}
          />
          <span className="learning-hub-block-player__video-time">
            {formatVideoTime(duration)}
          </span>

          <Menu>
            <MenuButton
              type="button"
              className="learning-hub-block-player__video-icon-btn learning-hub-block-player__video-gear-btn"
              aria-label="Playback speed"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22l-1.92 3.32c-.11.2-.06.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
                />
              </svg>
            </MenuButton>
            <MenuPopover
              position={positionRight}
              className="learning-hub-block-player__video-rate-popover"
            >
              <MenuItems className="learning-hub-block-player__video-rate-menu">
                {PLAYBACK_RATES.map((rate) => (
                  <MenuItem
                    key={rate}
                    className="learning-hub-block-player__video-rate-item"
                    onSelect={() => setPlaybackRate(rate)}
                  >
                    {rate === playbackRate ? '✓ ' : ''}
                    {rate === 1 ? 'Normal' : `${rate}×`}
                  </MenuItem>
                ))}
              </MenuItems>
            </MenuPopover>
          </Menu>
        </div>
      </div>
      <div className="learning-hub-block-player__progress-actions">
        <button
          type="button"
          className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
          disabled={saving}
          onClick={async () => {
            const el = videoRef.current
            const progress =
              el && Number.isFinite(el.currentTime)
                ? el.currentTime
                : currentTime
            setSaving(true)
            try {
              await saveLearningHubBlockProgress(block.id, {
                type: 'video',
                progress: progress,
              })
            } finally {
              setSaving(false)
            }
          }}
        >
          {saving ? 'Saving...' : 'Save progress'}
        </button>
      </div>
    </div>
  )
}

LearningHubVideoBlockPanel.propTypes = {
  block: PropTypes.object.isRequired,
  fetchLearningHubBlock: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

export default compose(
  connect(null, {
    fetchLearningHubBlock,
    flashErrorMessage: flashActions.flashErrorMessage,
  })
)(LearningHubVideoBlockPanel)
